import React, { useState, useEffect, useRef } from 'react';
import airFreightImg from '../assets/service/air-freight.png';
import oceanFreightImg from '../assets/service/ocean-freight.png';
import warehousingImg from '../assets/service/Warehousing.png';
import inlandTransImg from '../assets/service/Inland-Transportation.png';
import customsClearenceImg from '../assets/service/Customs-Clearance.png';
import { Link } from 'react-router-dom';

// Add TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const cards = [
  { 
    image: airFreightImg, 
    title: 'Air Freight', 
    description: 'Fast and reliable air cargo services worldwide',
    id: 'air-freight'
  },
  { 
    image: oceanFreightImg, 
    title: 'Ocean Freight', 
    description: 'Full-container and consolidated sea shipping',
    id: 'ocean-freight'
  },
  { 
    image: inlandTransImg, 
    title: 'Road Freight', 
    description: 'Efficient road and rail transport across regions',
    id: 'road-freight'
  },
  { 
    image: warehousingImg, 
    title: 'Warehousing', 
    description: 'Secure and efficient warehousing solutions',
    id: 'warehousing-inventory'
  },
  { 
    image: customsClearenceImg, 
    title: 'Customs Clearance', 
    description: 'Expert customs clearance to navigate regulations',
    id: 'customs-compliance'
  },
];

const Services = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [inView, setInView] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);

          // Animate cards one by one with delays
          let delay = 0;
          const delays = [];
          cards.forEach((_, i) => {
            delays.push(setTimeout(() => {
              setVisibleCards(prev => [...prev, i]);
            }, delay));
            delay += 200; // 200ms between each card
          });

          return () => delays.forEach(t => clearTimeout(t));
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full min-h-screen pb-10 text-white relative overflow-hidden"
    >
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

         @keyframes fadeInTo20 {
            from {
              opacity: 0;
            }
            to {
              opacity: 0.2;
            }
          }
          
          @keyframes fadeInScale {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          
          .animate-fade-up {
            animation: fadeInUp 0.8s ease-out forwards;
          }
          
          .animate-fade-in {
            animation: fadeIn 1s ease-out forwards;
          }

         .animate-fade-in-to-20 {
            animation: fadeInTo20 1.2s ease-out forwards;
          }
          
          .animate-fade-scale {
            animation: fadeInScale 0.6s ease-out forwards;
          }
        `}
      </style>

      <div className="relative text-center py-8 md:py-10 mt-4 md:mt-14 2xl:py-10 2xl:mt16">
        {/* Fading SOLUTIONS hollow text */}
        <h2
          className={`absolute top-1/5 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[8vw] sm:text-[6vw] lg:text-[60px] 2xl:text-[80px] font-extrabold text-transparent md:tracking-wide select-none z-0 ${inView ? 'animate-fade-in-to-20' : 'opacity-0'
            }`}
          style={{
            WebkitTextStroke: '2px white',
          }}
        >
          SOLUTIONS
        </h2>

        {/* Foreground animated text */}
        <div className="relative z-10">
          <h2 className={`text-3xl sm:text-5xl md:text-6xl lg:text-7xl 2xl:text-8xl font-[500] transition-opacity duration-700 ${inView ? 'opacity-100 animate-fade-up' : 'opacity-0'
            }`}>
            {inView && (
              <TypewriterText
                text="From Air to Ocean"
                isActive={inView}
                delay={0}
              />
            )}
          </h2>
          <p className={`font-light text-base sm:text-xl md:text-2xl 2xl:text-4xl mt-2 md:mt-4 2xl:m-8 transition-opacity duration-700 delay-200 ${inView ? 'opacity-100 animate-fade-up' : 'opacity-0'
            }`}>
            We offer end-to-end logistics solutions, including
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-3 2xl:gap-4 px-2 sm:px-4 md:px-6 mt-10 md:mt-[20px]">
        {cards.map((card, index) => {
          const isActive = activeIndex === index;
          const isCardVisible = visibleCards.includes(index);

          return (
            <div
              key={index}
              onClick={() => setActiveIndex(index)}
              className={`
                relative transition-all duration-300 ease-in-out   overflow-hidden cursor-pointer
                ${isActive
                  ? 'rounded-3xl lg:rounded-[48px]'
                  : 'rounded-2xl lg:rounded-[24px]'
                }
                ${isActive ?
                  (isMobile ? 'w-full h-[350px]' : 'w-[80vw] md:w-[700px] h-[350px] md:h-[500px] 2xl:w-[900px] 2xl:h-[60vh] rounded-3xl') :
                  (isMobile ? 'w-full h-[60px]' : 'w-[40px] sm:w-[60px] md:w-[85px] h-[350px] md:h-[500px] 2xl:w-[105px] 2xl:h-[60vh]')
                }
                ${isCardVisible ? 'animate-fade-scale' : 'opacity-0'}
              `}
              style={{
                backgroundImage: `url(${card.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                animationDelay: `${index * 200}ms`
              }}
            >
              <div className={`absolute inset-0 bg-black bg-opacity-70 ${isActive ? 'rounded-2xl 2xl:rounded-[48px]' : 'rounded-xl 2xl:rounded-[20px]'}`} />

              {isActive ? (
                <div className="absolute inset-0 flex flex-col justify-center items-start pl-4 sm:pl-8 md:pl-16 lg:pl-24 text-left z-10">
                  <span className="text-lg sm:text-xl md:text-2xl 2xl:text-4xl font-semibold mb-4 md:mb-10">{`0${index + 1}`}</span>
                  <div className="flex flex-col md:flex-col items-start max-w-xl">
                    <h3 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl 2xl:text-8xl font-bold mr-2">
                      {card.title.split(' ')[0]}
                    </h3>
                    <h4 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl 2xl:text-6xl">
                      {card.title.split(' ')[1]}
                    </h4>
                  </div>
                  <p className="text-sm sm:text-base md:text-lg 2xl:text-2xl max-w-xs mt-4 mb-4 md:mb-8 font-thin">
                    {card.description}
                  </p>
                  <Link
                    to={`/Services#${card.id}`}
                    className="group flex items-center gap-1 text-white text-base md:text-lg 2xl:text-2xl font-thin hover:cursor-pointer"
                  >
                    Learn More
                    <svg
                      className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 transform transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-between z-10 p-2 text-white">
                  {isMobile ? (
                    <div className="w-full flex items-center justify-between px-4">
                      <span className="text-lg  font-semibold">{`0${index + 1}`}</span>
                      <button className="group w-8 h-8 flex items-center justify-center rounded-2xl bg-[#7f7f7e] transition duration-200 hover:bg-white">
                        {/* Downward-pointing arrow for mobile */}
                        <svg
                          className="w-5 h-5 text-white group-hover:text-black transition duration-200"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="text-base sm:text-lg md:text-xl 2xl:text-4xl font-semibold mt-4 md:mt-7">{`0${index + 1}`}</span>
                      <button className="group mb-4 md:mb-6 w-8 h-6 md:w-10 md:h-8 2xl:w-14 2xl:h-10 flex items-center justify-center rounded-2xl bg-[#7f7f7e] transition duration-200 hover:bg-white">
                        {/* Right-pointing arrow for desktop */}
                        <svg
                          className="w-5 h-5 2xl:w-8 2xl:h-8 text-white group-hover:text-black transition duration-200"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 12h14m-4-6l4 6-4 6"
                          />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Services;